import { Dimensions, StyleSheet, Text, View } from 'react-native';
import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Blur,
  Circle,
  ColorMatrix,
  Group,
  Paint,
  SweepGradient,
  vec,
} from '@shopify/react-native-skia';
import Touchable, { useGestureHandler } from 'react-native-skia-gesture';
import {
  runOnJS,
  SharedValue,
  useAnimatedReaction,
  useDerivedValue,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import { toast } from 'sonner';

import { AnimatedSlider, SliderRef } from './animated-slider';

import { InteractiveForwardRefProps } from '@/components/interactive';
import { Colors } from '@/constants/blog-colors';

const RADIUS = 80;

type MetaballProps = {
  type: 'basic' | 'with-pan' | 'with-blur' | 'with-color-matrix';
};

type SliderContainerProps = {
  label: string;
  sliderRef: React.RefObject<SliderRef>;
  value: SharedValue<number>;
  initialProgress: number;
  minValue?: number;
  maxValue?: number;
};
const SliderContainer: React.FC<SliderContainerProps> = ({
  label,
  sliderRef,
  value,
  initialProgress,
  minValue = 0,
  maxValue = 100,
}) => {
  const [progress, setProgress] = useState(initialProgress);
  const labelText = label + ' (' + progress.toFixed(0) + ')';

  // Create a local shared value to avoid mutating props
  const localValue = useSharedValue(initialProgress);

  useAnimatedReaction(
    () => localValue.value,
    (current) => {
      value.value = current;
    }
  );

  const updateProgress = useCallback((progress: number) => {
    setProgress(progress);
  }, []);

  const handleUpdate = useCallback(
    (progress: number) => {
      'worklet';
      localValue.value = progress;
      runOnJS(updateProgress)(progress);
    },
    [localValue, updateProgress]
  );

  return (
    <View style={styles.sliderContainer}>
      <Text style={styles.label} numberOfLines={1} adjustsFontSizeToFit>
        {labelText}
      </Text>
      <AnimatedSlider
        style={styles.slider}
        minValue={minValue}
        maxValue={maxValue}
        ref={sliderRef}
        onUpdate={handleUpdate}
        initialProgress={initialProgress}
      />
    </View>
  );
};

export const Metaball = React.forwardRef<
  InteractiveForwardRefProps,
  MetaballProps
>(({ type }, ref) => {
  const canvasSize = useSharedValue({ width: -200, height: -200 });

  const cx = useSharedValue(canvasSize.value.width / 2);
  const cy = useSharedValue(canvasSize.value.height / 2);

  const blur = useSharedValue(30);
  const alpha = useSharedValue(60);
  const position = useSharedValue(30);

  const blurSliderRef = useRef<SliderRef>(null);
  const alphaSliderRef = useRef<SliderRef>(null);
  const positionSliderRef = useRef<SliderRef>(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      blurSliderRef.current?.setProgress(30);
      alphaSliderRef.current?.setProgress(60);
      positionSliderRef.current?.setProgress(30);
    },
    play: () => {
      //
    },
    pause: () => {},
  }));

  useAnimatedReaction(
    () => canvasSize.value,
    (size) => {
      cx.value = size.width / 2;
      cy.value = size.height / 2;
    }
  );

  const context = useSharedValue({ x: 0, y: 0 });
  const showToast = useCallback(() => {
    toast.info('Slooow down - Continue reading 👀', {
      description: 'The Pan Gesture implementation is in the next demo 👇',
      duration: 3000,
      position: 'bottom-center',
      dismissible: true,
    });
  }, []);

  const gestureHandler = useGestureHandler({
    onStart: (_) => {
      'worklet';
      if (type === 'basic') {
        runOnJS(showToast)();
        return;
      }
      context.value = { x: cx.value, y: cy.value };
    },
    onActive: ({ translationX, translationY }) => {
      'worklet';
      if (type === 'basic') {
        return;
      }

      cx.value = translationX + context.value.x;
      cy.value = translationY + context.value.y;
    },
    onEnd: () => {
      'worklet';
      if (type === 'basic') {
        return;
      }

      cx.value = withSpring(canvasSize.value.width / 2);
      cy.value = withSpring(canvasSize.value.height / 2);
    },
  });

  const colorMatrix = useDerivedValue(() => {
    // prettier-ignore
    return [
      // R, G, B, A, Bias (Offset)
      1, 0, 0, 0, 0,
      // prettier-ignore
      0, 1, 0, 0, 0,
      // prettier-ignore
      0, 0, 1, 0, 0,
      // prettier-ignore
      0, 0, 0, alpha.value, -position.value,
    ];
  });

  const layer = useMemo(() => {
    if (type === 'basic' || type === 'with-pan') {
      return <Paint />;
    }

    return (
      <Paint>
        {/* pixelOpacity > blurredOpacity * 60 - 30 */}
        <Blur blur={blur} />
        {type === 'with-color-matrix' && <ColorMatrix matrix={colorMatrix} />}
      </Paint>
    );
  }, [blur, colorMatrix, type]);

  const centeredCircleX = useDerivedValue(() => canvasSize.value.width / 2);
  const centeredCircleY = useDerivedValue(() => canvasSize.value.height / 2);

  return (
    <View style={styles.fill}>
      <Touchable.Canvas onSize={canvasSize} style={styles.fill}>
        <Group layer={layer}>
          <Touchable.Circle {...gestureHandler} cx={cx} cy={cy} r={RADIUS} />
          <Circle cx={centeredCircleX} cy={centeredCircleY} r={RADIUS} />
          <SweepGradient c={vec(0, 0)} colors={['cyan', 'magenta', 'cyan']} />
        </Group>
      </Touchable.Canvas>
      {type === 'with-color-matrix' && (
        <View style={styles.slidersContainer}>
          <SliderContainer
            label="blur"
            sliderRef={blurSliderRef}
            value={blur}
            initialProgress={30}
          />
          <SliderContainer
            label="alpha"
            sliderRef={alphaSliderRef}
            value={alpha}
            initialProgress={60}
            minValue={1}
            maxValue={100}
          />
          <SliderContainer
            label="pos"
            sliderRef={positionSliderRef}
            value={position}
            initialProgress={30}
          />
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
  slider: {
    width: Math.min(Dimensions.get('window').width * 0.5, 250),
    height: 2,
  },
  slidersContainer: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 50,
  },
  sliderContainer: {
    height: 45,
    alignItems: 'center',
    flexDirection: 'row',
    width: Math.min(Dimensions.get('window').width * 0.7, 350),
    justifyContent: 'flex-start',
  },
  label: {
    fontSize: 12,
    fontFamily: 'monospace',
    color: Colors.paragraph,
    width: '20%',
    minWidth: 90,
  },
});
